import {Async, fromResult, isLoaded, Loaded} from 'Core/models/Async';
import {Email, UnvalidatedEmail, ValidEmail} from 'Core/models/Email';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {Password, UnvalidatedPassword, ValidPassword} from 'Core/models/Password';
import {EmptyPhoneNumber, PhoneNumber, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {SecurityQuestion, UnvalidatedSecurityQuestion, ValidSecurityQuestion} from 'Core/models/SecurityQuestion';
import * as E from 'fp-ts/lib/Either';
import {Either, isRight, left, Right} from 'fp-ts/lib/Either';
import {flow} from 'fp-ts/lib/function';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

class StringWithAtLeast10Characters {
  public static create(value: string) {
    const validatedString = NonEmptyString.create(value);

    return validatedString instanceof NonEmptyString && validatedString.value.length >= 10
      ? new StringWithAtLeast10Characters(value)
      : validatedString;
  }

  public readonly key = 'StringWithAtLeast10Characters ';

  private constructor(public readonly value: string) {}
}

export type UserRegistrationUsername = StringWithAtLeast10Characters | StringUnion;

const validateUserName = flow(StringWithAtLeast10Characters.create, E.right, fromResult);

export class ValidUserRegistration {
  public static create(
    email: Async<Either<Email, ValidEmail>>,
    userName: Async<Either<StringUnion, UserRegistrationUsername>>,
    phoneNumber: Async<Either<PhoneNumber, ValidPhoneNumber>>,
    password: Password,
    securityQuestionOne: SecurityQuestion,
    securityQuestionTwo: SecurityQuestion
  ): UserRegistration {
    const validatedUsername =
      isLoaded(userName) && isRight(userName.Data) ? validateUserName(userName.Data.right.value) : userName;

    return isLoaded(phoneNumber) &&
      isRight(phoneNumber.Data) &&
      phoneNumber.Data.right instanceof ValidPhoneNumber &&
      isLoaded(validatedUsername) &&
      isRight(validatedUsername.Data) &&
      validatedUsername.Data.right instanceof StringWithAtLeast10Characters &&
      isLoaded(email) &&
      isRight(email.Data) &&
      email.Data.right instanceof ValidEmail &&
      password instanceof ValidPassword &&
      securityQuestionOne instanceof ValidSecurityQuestion &&
      securityQuestionTwo instanceof ValidSecurityQuestion
      ? new ValidUserRegistration(
          email as Loaded<Right<ValidEmail>>,
          validatedUsername as Loaded<Right<StringWithAtLeast10Characters>>,
          phoneNumber as Loaded<Right<ValidPhoneNumber>>,
          password,
          securityQuestionOne,
          securityQuestionTwo
        )
      : new InvalidUserRegistration(
          email,
          validatedUsername,
          phoneNumber,
          password,
          securityQuestionOne,
          securityQuestionTwo
        );
  }

  public readonly key = 'Valid';
  private constructor(
    public readonly email: Loaded<Right<ValidEmail>>,
    public readonly userName: Loaded<Right<StringWithAtLeast10Characters>>,
    public readonly phoneNumber: Loaded<Right<ValidPhoneNumber>>,
    public readonly password: ValidPassword,
    public readonly securityQuestionOne: ValidSecurityQuestion,
    public readonly securityQuestionTwo: ValidSecurityQuestion
  ) {}
}

export class InvalidUserRegistration {
  public readonly key = 'Invalid';
  constructor(
    public readonly email: Async<Either<Email, ValidEmail>>,
    public readonly userName: Async<Either<StringUnion, UserRegistrationUsername>>,
    public readonly phoneNumber: Async<Either<PhoneNumber, ValidPhoneNumber>>,
    public readonly password: Password,
    public readonly securityQuestionOne: SecurityQuestion,
    public readonly securityQuestionTwo: SecurityQuestion
  ) {}
}

export class SavingUserRegistration {
  public static create(registration: ValidUserRegistration) {
    return new SavingUserRegistration(
      registration.email,
      registration.userName,
      registration.phoneNumber,
      registration.password,
      registration.securityQuestionOne,
      registration.securityQuestionTwo
    );
  }
  public readonly key = 'Saving';

  private constructor(
    public readonly email: Loaded<Right<ValidEmail>>,
    public readonly userName: Loaded<Right<StringWithAtLeast10Characters>>,
    public readonly phoneNumber: Loaded<Right<ValidPhoneNumber>>,
    public readonly password: ValidPassword,
    public readonly securityQuestionOne: ValidSecurityQuestion,
    public readonly securityQuestionTwo: ValidSecurityQuestion
  ) {}
}

export class UserRegistrationWithError {
  public static create(registration: ValidUserRegistration, errorMessage: string) {
    return new UserRegistrationWithError(
      registration.email,
      registration.userName,
      registration.phoneNumber,
      registration.password,
      registration.securityQuestionOne,
      registration.securityQuestionTwo,
      errorMessage
    );
  }
  public readonly key = 'RegistrationWithError';

  private constructor(
    public readonly email: Loaded<Right<ValidEmail>>,
    public readonly userName: Loaded<Right<StringWithAtLeast10Characters>>,
    public readonly phoneNumber: Loaded<Right<ValidPhoneNumber>>,
    public readonly password: ValidPassword,
    public readonly securityQuestionOne: ValidSecurityQuestion,
    public readonly securityQuestionTwo: ValidSecurityQuestion,
    public readonly errorMessage: string
  ) {}
}

export type UserRegistrationOptions<TReturn> =
  | (ToMatchOptions<UserRegistration, 'key', TReturn> & {default: undefined})
  | {
      Saving?: (registration: SavingUserRegistration) => TReturn;
      RegistrationWithError?: (registration: UserRegistrationWithError) => TReturn;
      Valid?: (registration: ValidUserRegistration) => TReturn;
      Invalid?: (registration: InvalidUserRegistration) => TReturn;
      default: (registration: UserRegistration) => TReturn;
    };

export type UserRegistration =
  | ValidUserRegistration
  | InvalidUserRegistration
  | SavingUserRegistration
  | UserRegistrationWithError;

export const matchUserRegistration = <TReturn>(
  userRegistration: UserRegistration,
  options: UserRegistrationOptions<TReturn>
) => {
  switch (userRegistration.key) {
    case 'Saving':
      return options.Saving && options.Saving(userRegistration);
    case 'RegistrationWithError':
      return options.RegistrationWithError && options.RegistrationWithError(userRegistration);
    case 'Valid':
      return options.Valid && options.Valid(userRegistration);
    case 'Invalid':
      return options.Invalid && options.Invalid(userRegistration);
    default:
      return options.default && options.default(userRegistration);
  }
};

export const EmptyRegistration = new InvalidUserRegistration(
  new Loaded(left(new UnvalidatedEmail(''))),
  new Loaded(left(new EmptyString())),
  new Loaded(left(new EmptyPhoneNumber())),
  new UnvalidatedPassword('', ''),
  new UnvalidatedSecurityQuestion('', ''),
  new UnvalidatedSecurityQuestion('', '')
);
